import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radio, Mic, MicOff, Video, VideoOff, Camera, Volume2, Bot, MessageSquare, ShoppingBag,
  Sparkles, Send, UserPlus, QrCode, Zap, X, Settings, Share2, ScreenShare, Users, PhoneOff,
} from "lucide-react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const rm = (n) => "RM " + (n || 0).toLocaleString("en-MY");

const CAT_STYLE = {
  QUESTION: "bg-blue-500/20 text-blue-400 border-blue-500/40",
  BUYING_INTENT: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  COMPLAINT: "bg-rose-500/20 text-rose-400 border-rose-500/40",
  SPAM: "bg-slate-600/30 text-slate-400 border-slate-600/40",
  HOT_LEAD: "bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse",
};
const PLAT_COLOR = { Facebook: "text-blue-400", YouTube: "text-red-400", TikTok: "text-fuchsia-400" };

export default function Studio() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();

  const [session, setSession] = useState(null);
  const [products, setProducts] = useState([]);
  const [comments, setComments] = useState([]);
  const [inLobby, setInLobby] = useState(true);

  // media
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState({ cams: [], mics: [], spks: [] });
  const [sel, setSel] = useState({ cam: "", mic: "", spk: "" });
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [micLevel, setMicLevel] = useState(0);
  const [audioFx, setAudioFx] = useState({ echo: true, noise: true, gain: true });

  // console state
  const [activeScene, setActiveScene] = useState("host");
  const [pinnedProduct, setPinnedProduct] = useState(null);
  const [pinnedComment, setPinnedComment] = useState(null);
  const [tab, setTab] = useState("ai");
  const [insights, setInsights] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiReply, setAiReply] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showGuest, setShowGuest] = useState(false);

  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const loadSession = useCallback(async () => {
    if (!sid) {
      // create fresh
      const res = await api.post("/sessions", { title: "Untitled Live", type: "Live Shopping", host: "Me" });
      navigate(`/studio/${res.data.id}`, { replace: true });
      return;
    }
    const [s, p, c] = await Promise.all([
      api.get(`/sessions/${sid}`), api.get("/products"), api.get(`/comments?session_id=${sid}`),
    ]);
    setSession(s.data); setProducts(p.data); setComments(c.data); setActiveScene(s.data.active_scene || "host");
  }, [sid, navigate]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // enumerate + start preview
  const startMedia = useCallback(async (camId, micId) => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((tk) => tk.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: camId ? { deviceId: { exact: camId } } : true,
        audio: {
          deviceId: micId ? { exact: micId } : undefined,
          echoCancellation: audioFx.echo, noiseSuppression: audioFx.noise, autoGainControl: audioFx.gain,
        },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cams: all.filter((d) => d.kind === "videoinput"),
        mics: all.filter((d) => d.kind === "audioinput"),
        spks: all.filter((d) => d.kind === "audiooutput"),
      });
      // mic meter
      if (audioCtxRef.current) audioCtxRef.current.close();
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ac;
      const source = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const arr = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(arr);
        const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
        setMicLevel(Math.min(100, Math.round((avg / 140) * 100)));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      toast.error("Camera/microphone access denied or unavailable. Allow permissions to broadcast.");
    }
  }, [audioFx]);

  useEffect(() => {
    startMedia();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close();
      if (streamRef.current) streamRef.current.getTracks().forEach((tk) => tk.stop());
    };
    // eslint-disable-next-line
  }, []);

  // live timer + simulated comment stream
  useEffect(() => {
    if (session?.status !== "live") return;
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    const gen = setInterval(() => generateComments(), 15000);
    return () => { clearInterval(timer); clearInterval(gen); };
    // eslint-disable-next-line
  }, [session?.status]);

  const toggleMic = () => {
    const on = !micOn; setMicOn(on);
    streamRef.current?.getAudioTracks().forEach((tk) => (tk.enabled = on));
  };
  const toggleCam = () => {
    const on = !camOn; setCamOn(on);
    streamRef.current?.getVideoTracks().forEach((tk) => (tk.enabled = on));
  };

  const changeScene = async (key) => {
    setActiveScene(key);
    if (key === "product" && !pinnedProduct) setPinnedProduct(products.find((p) => p.type !== "property") || products[0]);
    try { await api.patch(`/sessions/${sid}/scene`, { active_scene: key }); } catch (e) {}
  };

  const goLive = async () => {
    try { const res = await api.post(`/sessions/${sid}/go-live`); setSession(res.data); setElapsed(0); toast.success("You are LIVE on Facebook & YouTube"); askProducer(); }
    catch (e) { toast.error("Failed to go live"); }
  };
  const endLive = async () => {
    try { await api.post(`/sessions/${sid}/end`); toast.success("Live ended. Generating AI clips…"); navigate("/clips"); }
    catch (e) { toast.error("Failed to end"); }
  };

  const askProducer = async () => {
    setAiLoading(true);
    try { const res = await api.post("/ai/producer", { session_id: sid }); setInsights(res.data.insights || []); }
    catch (e) { toast.error("AI Producer unavailable"); } finally { setAiLoading(false); }
  };

  const generateComments = async () => {
    try {
      const facts = products.map((p) => p.title).join(", ");
      const res = await api.post("/comments/generate", { session_id: sid, product_context: facts });
      setComments((prev) => [...res.data.comments, ...prev]);
    } catch (e) {}
  };

  const pinComment = async (c) => {
    setPinnedComment(c); setActiveScene("host");
    try { await api.post(`/comments/${c.id}/pin`); } catch (e) {}
    toast.success("Comment shown on screen");
  };

  const askSalesAssistant = async (question) => {
    setReplyLoading(true); setAiReply(null); setTab("ai");
    try { const res = await api.post("/ai/sales-assistant", { question }); setAiReply({ question, reply: res.data.reply }); }
    catch (e) { toast.error("AI unavailable"); } finally { setReplyLoading(false); }
  };

  const createLead = async (c) => {
    try {
      await api.post("/leads", { name: c.author, phone: "", session_id: sid, product: products[0]?.title, intent: c.intent, source: "Yes Go Live" });
      toast.success(`Lead created: ${c.author}`);
    } catch (e) { toast.error("Failed"); }
  };

  const pinProduct = (p) => { setPinnedProduct(p); setActiveScene("product"); toast.success(`${p.title} pinned to live`); changeScene("product"); };

  const doAction = (action) => {
    if (action === "show_product") pinProduct(products.find((p) => p.type !== "property") || products[0]);
    else if (action === "show_cta") { setActiveScene("qr_cta"); changeScene("qr_cta"); }
    else if (action === "create_offer") { pinProduct(products.find((p) => p.type === "offer") || products[0]); }
    else if (action === "answer_question") { const q = comments.find((c) => c.category === "QUESTION"); if (q) askSalesAssistant(q.text); setTab("comments"); }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (!session) return <AppLayout active="studio"><div className="animate-pulse text-muted-foreground font-mono">Preparing studio…</div></AppLayout>;

  // ---------------- Pre-Live Lobby ----------------
  if (inLobby) {
    return (
      <AppLayout active="studio">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{t("prelive_lobby")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{session.title}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="relative aspect-video bg-black">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="lobby-video" />
              {!camOn && <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-muted-foreground"><VideoOff className="h-8 w-8" /></div>}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                <button data-testid="lobby-mic-toggle" onClick={toggleMic} className={`h-10 w-10 rounded-full flex items-center justify-center ${micOn ? "bg-white/15 text-white" : "bg-[#E5253A] text-white"}`}>{micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</button>
                <button data-testid="lobby-cam-toggle" onClick={toggleCam} className={`h-10 w-10 rounded-full flex items-center justify-center ${camOn ? "bg-white/15 text-white" : "bg-[#E5253A] text-white"}`}>{camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}</button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <Selector icon={Camera} label={t("camera")} value={sel.cam} options={devices.cams} onChange={(v) => { setSel((s) => ({ ...s, cam: v })); startMedia(v, sel.mic); }} testid="camera-select" />
              <Selector icon={Mic} label={t("microphone")} value={sel.mic} options={devices.mics} onChange={(v) => { setSel((s) => ({ ...s, mic: v })); startMedia(sel.cam, v); }} testid="mic-select" />
              <Selector icon={Volume2} label={t("speaker")} value={sel.spk} options={devices.spks} onChange={(v) => setSel((s) => ({ ...s, spk: v }))} testid="speaker-select" />
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span>{t("mic_level")}</span><span className="font-mono">{micLevel}%</span></div>
                <div className="h-2 rounded-full bg-accent overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${micLevel}%` }} data-testid="mic-level-bar" /></div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                {["echo", "noise", "gain"].map((k) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={audioFx[k]} onChange={() => { setAudioFx((f) => ({ ...f, [k]: !f[k] })); }} data-testid={`fx-${k}`} className="accent-[#E5253A]" />
                    {t(k)}
                  </label>
                ))}
              </div>
              <button data-testid="enter-studio-btn" onClick={() => setInLobby(false)} className="w-full h-12 rounded-xl bg-[#E5253A] hover:bg-[#c71f31] text-white font-bold flex items-center justify-center gap-2">
                <Radio className="h-5 w-5" /> {t("enter_studio")}
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ---------------- Studio Console ----------------
  const isLive = session.status === "live";
  const stats = { viewers: isLive ? session.viewers : 0, comments: comments.length, leads: session.leads, revenue: session.revenue };

  return (
    <AppLayout active="studio">
      <div className="grid lg:grid-cols-[220px_1fr_360px] gap-4">
        {/* Left: scenes + control */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="text-xs font-bold text-muted-foreground mb-2 px-1">{t("scenes")}</div>
            <div className="grid grid-cols-2 gap-2">
              {session.scenes.map((sc, i) => (
                <button key={sc.key} data-testid={`scene-switch-${sc.key}`} onClick={() => changeScene(sc.key)}
                  className={`text-left px-2.5 py-2 rounded-lg text-[11px] font-medium border transition-colors ${activeScene === sc.key ? "border-[#E5253A] bg-[#E5253A]/10 text-[#E5253A]" : "border-border hover:bg-accent"}`}>
                  <span className="font-mono text-[9px] opacity-60 block">{String(i + 1).padStart(2, "0")}</span>
                  {sc.name}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-3">
            <div className="text-xs font-bold text-muted-foreground mb-2 px-1">{t("audio_mixer")}</div>
            {[["Host", micLevel], ["Guest", 0], ["Music", 20], ["Video", 40]].map(([n, v]) => (
              <div key={n} className="mb-2">
                <div className="flex justify-between text-[10px] text-muted-foreground"><span>{n}</span></div>
                <div className="h-1.5 rounded-full bg-accent overflow-hidden"><div className="h-full bg-cyan-500" style={{ width: `${v}%` }} /></div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-card p-3 space-y-2">
            <button data-testid="invite-guest-btn" onClick={() => setShowGuest(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-accent text-xs font-medium"><UserPlus className="h-3.5 w-3.5" /> {t("invite_guest")}</button>
            <button data-testid="add-source-btn" onClick={() => toast.info("Add source: OBS Virtual Cam · RTMP/SRT · Mobile QR (Business plan)")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-accent text-xs font-medium"><ScreenShare className="h-3.5 w-3.5" /> {t("add_source")}</button>
          </div>
        </div>

        {/* Center: preview + controls */}
        <div className="space-y-4 min-w-0">
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="flex items-center gap-2 text-xs font-mono">
                {isLive ? <><span className="h-2 w-2 rounded-full bg-[#E5253A] live-dot" /> ON AIR <span className="text-muted-foreground">{fmtTime(elapsed)}</span></> : <span className="text-muted-foreground">PREVIEW · OFFLINE</span>}
              </div>
              <div className="flex gap-1.5">
                {session.destinations.map((d) => (
                  <span key={d.platform} title={d.status} className={`px-2 py-0.5 rounded text-[10px] font-mono ${d.status === "connected" ? "bg-emerald-500/15 text-emerald-400" : d.status === "eligibility_pending" ? "bg-amber-500/15 text-amber-400" : "bg-accent text-muted-foreground"}`}>
                    {d.platform.split(" ")[0]}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative aspect-video bg-black">
              <video ref={videoRef} autoPlay muted playsInline className={`w-full h-full object-cover ${activeScene === "host_guest" ? "opacity-95" : ""}`} data-testid="studio-video" />
              {!camOn && <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-muted-foreground"><VideoOff className="h-8 w-8" /></div>}

              {/* Live badge + logo */}
              {isLive && <div className="absolute top-3 left-3 px-2 py-1 rounded bg-[#E5253A] text-white text-[10px] font-bold">● LIVE</div>}
              <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/50 text-white text-[10px] font-bold tracking-wide">YES GO LIVE</div>

              {/* Lower third (host / host_guest) */}
              {["host", "host_guest", "opening", "closing"].includes(activeScene) && (
                <div className="absolute bottom-10 left-3 px-3 py-2 rounded-lg bg-black/60 text-white fade-in">
                  <div className="text-sm font-bold">{session.host}</div>
                  <div className="text-[10px] opacity-80">{activeScene === "opening" ? "Welcome" : activeScene === "closing" ? "Thank you!" : "Live Host"}</div>
                </div>
              )}

              {/* Product overlay */}
              {activeScene === "product" && pinnedProduct && (
                <div className="absolute bottom-10 right-3 w-56 rounded-xl bg-card/95 border border-border p-3 fade-in">
                  <div className="text-[9px] font-mono text-muted-foreground">LIVE {pinnedProduct.type.toUpperCase()}</div>
                  <div className="text-sm font-bold mt-0.5">{pinnedProduct.title}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    {pinnedProduct.normal_price && <span className="text-xs line-through text-muted-foreground font-mono">RM{pinnedProduct.normal_price}</span>}
                    <span className="text-lg font-mono font-bold text-emerald-400">{rm(pinnedProduct.price)}</span>
                  </div>
                  <button className="mt-2 w-full h-8 rounded-lg bg-[#E5253A] text-white text-xs font-bold">BUY NOW</button>
                </div>
              )}

              {/* Promotion */}
              {activeScene === "promotion" && (
                <div className="absolute inset-x-0 bottom-16 mx-auto w-max px-6 py-3 rounded-xl bg-amber-500 text-black text-center fade-in">
                  <div className="text-[10px] font-bold">🔥 FLASH PROMO</div>
                  <div className="text-2xl font-mono font-extrabold">{rm(products[0]?.price)}</div>
                </div>
              )}

              {/* QR / CTA */}
              {activeScene === "qr_cta" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white fade-in">
                  <div className="h-28 w-28 rounded-xl bg-white p-2 flex items-center justify-center"><QrCode className="h-full w-full text-black" /></div>
                  <div className="mt-3 text-sm font-bold">{t("scan_qr")}</div>
                  <div className="text-[11px] font-mono opacity-70">otosubs.live/{sid?.slice(0, 6)}</div>
                </div>
              )}

              {/* Testimonial */}
              {activeScene === "testimonial" && (
                <div className="absolute inset-x-6 bottom-14 px-4 py-3 rounded-xl bg-cyan-500/90 text-black fade-in">
                  <div className="text-[10px] font-bold">⭐ TESTIMONIAL</div>
                  <div className="text-sm font-medium">"Best Umrah experience, sangat teratur!" — Pn. Aminah</div>
                </div>
              )}

              {/* Pinned comment */}
              {pinnedComment && (
                <div className="absolute top-14 left-3 max-w-xs px-3 py-2 rounded-lg bg-black/70 text-white fade-in">
                  <div className={`inline-block text-[9px] font-mono ${PLAT_COLOR[pinnedComment.platform]}`}>{pinnedComment.platform}</div>
                  <div className="text-xs"><span className="font-bold">{pinnedComment.author}:</span> {pinnedComment.text}</div>
                </div>
              )}

              {/* Ticker */}
              <div className="absolute bottom-0 inset-x-0 bg-[#E5253A] text-white text-[11px] py-1 overflow-hidden whitespace-nowrap">
                <span className="inline-block animate-[marquee_18s_linear_infinite] px-4 font-mono">YES GO LIVE · Umrah December RM8,990 · Seats limited · WhatsApp us to book · Scan QR for live promo · </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between gap-2 p-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button data-testid="ctrl-mic" onClick={toggleMic} className={`h-10 w-10 rounded-lg flex items-center justify-center ${micOn ? "bg-accent" : "bg-[#E5253A] text-white"}`}>{micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</button>
                <button data-testid="ctrl-cam" onClick={toggleCam} className={`h-10 w-10 rounded-lg flex items-center justify-center ${camOn ? "bg-accent" : "bg-[#E5253A] text-white"}`}>{camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}</button>
                <button data-testid="ctrl-screen" onClick={() => toast.info("Screen share (simulated)")} className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center"><ScreenShare className="h-4 w-4" /></button>
                <button data-testid="ctrl-settings" onClick={() => setInLobby(true)} className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center"><Settings className="h-4 w-4" /></button>
              </div>
              {isLive ? (
                <button data-testid="end-live-btn" onClick={endLive} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white font-bold"><PhoneOff className="h-4 w-4" /> {t("end_live")}</button>
              ) : (
                <button data-testid="go-live-now-btn" onClick={goLive} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white font-bold"><Zap className="h-4 w-4" /> {t("go_live")}</button>
              )}
            </div>
          </div>

          {/* Control center metrics */}
          <div className="grid grid-cols-4 gap-px bg-border rounded-xl overflow-hidden border border-border">
            {[["Viewers", stats.viewers.toLocaleString()], ["Comments", stats.comments], ["Leads", stats.leads], ["Revenue", rm(stats.revenue)]].map(([k, v], i) => (
              <div key={k} className="bg-card p-3 text-center">
                <div className={`text-sm sm:text-base font-mono font-bold ${i === 3 ? "text-emerald-400" : ""}`}>{v}</div>
                <div className="text-[10px] text-muted-foreground">{k}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: tabs */}
        <div className="rounded-xl border border-border bg-card flex flex-col min-h-[500px]">
          <div className="grid grid-cols-3 border-b border-border">
            {[["ai", Bot, t("ai_producer")], ["comments", MessageSquare, t("comments")], ["commerce", ShoppingBag, t("commerce")]].map(([k, Icon, label]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${tab === k ? "text-[#E5253A] border-b-2 border-[#E5253A]" : "text-muted-foreground"}`}>
                <Icon className="h-4 w-4" /> {label.split(" ")[label.split(" ").length - 1]}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[560px]">
            {tab === "ai" && (
              <>
                <button data-testid="ask-producer-btn" onClick={askProducer} disabled={aiLoading} className="w-full flex items-center justify-center gap-2 h-9 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold disabled:opacity-60">
                  <Sparkles className="h-3.5 w-3.5" /> {aiLoading ? "Analyzing…" : t("ask_ai")}
                </button>
                {insights.map((ins, i) => (
                  <div key={i} data-testid={`insight-${i}`} className={`rounded-lg border p-3 text-xs fade-in ${ins.tone === "positive" ? "border-emerald-500/30 bg-emerald-500/5" : ins.tone === "warning" ? "border-amber-500/30 bg-amber-500/5" : "border-cyan-500/30 bg-cyan-500/5"}`}>
                    <div className="flex items-start gap-2"><Bot className="h-3.5 w-3.5 mt-0.5 shrink-0 text-cyan-400" /><span>{ins.message}</span></div>
                    {ins.suggested_action && ins.suggested_action !== "none" && (
                      <button data-testid={`ai-producer-action-${ins.suggested_action.replace(/_/g, "-")}`} onClick={() => doAction(ins.suggested_action)} className="mt-2 w-full h-8 rounded-lg bg-accent hover:bg-[#E5253A] hover:text-white text-[11px] font-semibold transition-colors capitalize">
                        {ins.suggested_action.replace(/_/g, " ")}
                      </button>
                    )}
                  </div>
                ))}
                {aiReply && (
                  <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 fade-in">
                    <div className="text-[10px] font-mono text-cyan-400 mb-1">{t("ai_reply")}</div>
                    <div className="text-[11px] text-muted-foreground mb-1">Q: {aiReply.question}</div>
                    <div className="text-xs">{aiReply.reply}</div>
                    <button data-testid="ai-reply-approve-send-btn" onClick={() => toast.success("Reply sent to viewer")} className="mt-2 w-full h-8 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-bold flex items-center justify-center gap-1"><Send className="h-3 w-3" /> {t("approve_send")}</button>
                  </div>
                )}
                {replyLoading && <div className="text-xs text-muted-foreground animate-pulse">AI drafting grounded reply…</div>}
                {/* quick actions */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button data-testid="quick-show-product" onClick={() => doAction("show_product")} className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold">{t("show_product")}</button>
                  <button data-testid="quick-show-cta" onClick={() => doAction("show_cta")} className="h-9 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-[11px] font-bold">{t("show_cta")}</button>
                </div>
              </>
            )}

            {tab === "comments" && (
              <>
                <button data-testid="generate-comments-btn" onClick={generateComments} className="w-full h-9 rounded-lg border border-border hover:bg-accent text-xs font-medium">+ Simulate incoming comments</button>
                {comments.map((c) => (
                  <div key={c.id} data-testid={`comment-${c.id}`} className="rounded-lg border border-border p-3 fade-in">
                    <div className="flex items-center justify-between">
                      <span className={`text-[9px] font-mono font-bold ${PLAT_COLOR[c.platform] || ""}`}>{c.platform}</span>
                      <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full border ${CAT_STYLE[c.category] || ""}`} data-testid={c.category === "HOT_LEAD" ? "comment-badge-hot-lead" : undefined}>{c.category?.replace("_", " ")}</span>
                    </div>
                    <div className="text-xs mt-1"><span className="font-bold">{c.author}:</span> {c.text}</div>
                    {["BUYING_INTENT", "HOT_LEAD"].includes(c.category) && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-accent overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${c.intent}%` }} /></div>
                        <span className="text-[9px] font-mono text-emerald-400">{c.intent}% {t("intent")}</span>
                      </div>
                    )}
                    <div className="mt-2 flex gap-1.5">
                      <button data-testid={`pin-comment-${c.id}`} onClick={() => pinComment(c)} className="flex-1 h-7 rounded-lg bg-accent hover:bg-[#E5253A] hover:text-white text-[10px] font-semibold transition-colors">Show</button>
                      {c.category === "QUESTION" && <button data-testid={`answer-comment-${c.id}`} onClick={() => askSalesAssistant(c.text)} className="flex-1 h-7 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-semibold">AI Answer</button>}
                      {["BUYING_INTENT", "HOT_LEAD"].includes(c.category) && <button data-testid={`create-lead-${c.id}`} onClick={() => createLead(c)} className="flex-1 h-7 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-semibold">{t("create_lead")}</button>}
                    </div>
                  </div>
                ))}
              </>
            )}

            {tab === "commerce" && (
              <>
                {products.map((p) => (
                  <div key={p.id} data-testid={`product-${p.id}`} className="rounded-lg border border-border p-3">
                    <div className="text-[9px] font-mono text-muted-foreground">{p.type.toUpperCase()}</div>
                    <div className="text-sm font-bold mt-0.5">{p.title}</div>
                    <div className="text-[11px] text-muted-foreground">{p.subtitle}</div>
                    <div className="flex items-baseline gap-2 mt-2">
                      {p.normal_price && <span className="text-xs line-through text-muted-foreground font-mono">RM{p.normal_price}</span>}
                      <span className="text-lg font-mono font-bold text-emerald-400">{rm(p.price)}</span>
                    </div>
                    <div className="text-[10px] font-mono text-amber-400 mt-1">
                      {p.seats_remaining != null ? `${t("seats_remaining")}: ${p.seats_remaining}` : p.stock != null ? `${t("stock")}: ${p.stock}` : ""}
                    </div>
                    <button data-testid={`live-commerce-pin-btn-${p.id}`} onClick={() => pinProduct(p)} className="mt-2 w-full h-8 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white text-[11px] font-bold">{t("pin_to_live")}</button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Guest modal */}
      {showGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowGuest(false)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold flex items-center gap-2"><Users className="h-4 w-4" /> {t("invite_guest")}</h3><button onClick={() => setShowGuest(false)}><X className="h-4 w-4" /></button></div>
            <p className="text-xs text-muted-foreground mb-3">Share this secure link. Guest opens it in a browser, enters name, tests camera/mic, and waits in the backstage room.</p>
            <div className="flex gap-2">
              <input readOnly value={`${window.location.origin}/live/${sid}?guest=1`} className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-xs font-mono" data-testid="guest-link-input" />
              <button data-testid="copy-guest-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/live/${sid}?guest=1`); toast.success("Link copied"); }} className="h-10 px-3 rounded-lg bg-[#E5253A] text-white text-xs font-bold flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Copy</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function Selector({ icon: Icon, label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm">
        <option value="">Default</option>
        {options.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${d.kind} ${d.deviceId.slice(0, 5)}`}</option>)}
      </select>
    </div>
  );
}
